import { cva, type VariantProps } from '@veraclins-dev/cva';

import {
  CHECKBOX_CLASSES,
  INPUT_CLASSES,
  INPUT_CONTAINER_CLASSES,
  OTP_INPUT_SLOT_CLASSES,
  RADIO_GROUP_ITEM_CLASSES,
} from './shared-classes';
import { extractStyleProps, styleProps } from './styles';

const shared = {
  responsive: {
    ...styleProps,
  },
  variants: {
    inputSize: {
      sm: 'min-h-8 px-2 py-1',
      md: 'min-h-10 px-3 py-2.25',
      lg: 'min-h-12 px-4 py-3',
      xl: 'min-h-14 px-5 py-4',
    },
  },
  defaultVariants: {
    inputSize: 'md',
  },
} as const;

// Input variants
const inputVariants = cva({
  base: INPUT_CLASSES,
  responsive: {
    ...shared.responsive,
  },
  variants: {
    ...shared.variants,
    // Surface/tone of the field. Defaults to `transparent` (inherits the
    // container) — `card` makes a card-background field first-class so fields
    // placed on a subtle page background no longer need a `bg-card!` override,
    // and `muted` gives a grey field with a hairline edge.
    surface: {
      transparent: '',
      card: 'bg-card',
      // A grey field with a hairline edge, such as a search box in a header.
      // It turns to the card fill while focused, under the focus border and
      // ring every field draws.
      muted:
        'bg-neutral-muted border-border shadow-none focus-visible:bg-card has-focus-visible:bg-card',
    },
  },
  defaultVariants: {
    ...shared.defaultVariants,
    surface: 'transparent',
  },
});

// The bordered box a labelled field (TextField and its siblings) draws around
// its input. It takes the same `surface` as the input, so `surface="card"` on
// the field fills the whole box, not only the input inside it.
const inputContainerVariants = cva({
  base: INPUT_CONTAINER_CLASSES,
  responsive: {
    ...shared.responsive,
  },
  variants: {
    ...shared.variants,
    surface: {
      transparent: '',
      card: 'bg-card',
      // A grey field with a hairline edge. It turns to the card fill while
      // something inside it has focus.
      muted:
        'bg-neutral-muted border-border shadow-none has-focus-visible:bg-card',
    },
  },
  defaultVariants: {
    ...shared.defaultVariants,
    surface: 'transparent',
  },
});

const otpInputSlotSizeVariants = {
  sm: 'size-8 p-2 text-base md:text-sm',
  md: 'size-10 p-3 text-base md:text-sm',
  lg: 'size-12 p-4 text-base',
  xl: 'size-14 p-5 text-lg',
} as const;

const otpInputSlotVariants = cva({
  base: OTP_INPUT_SLOT_CLASSES,
  ...shared,
  variants: {
    inputSize: otpInputSlotSizeVariants,
  },
});

const checkboxVariants = cva({
  base: CHECKBOX_CLASSES,
  responsive: {
    ...styleProps,
  },
  variants: {
    checkboxSize: {
      sm: 'size-4',
      md: 'size-5',
      lg: 'size-6',
      xl: 'size-7',
    },
  },
  defaultVariants: {
    checkboxSize: 'md',
  },
});

const radioVariants = {
  responsive: {
    ...styleProps,
  },
  variants: {
    radioSize: {
      sm: 'size-4',
      md: 'size-5',
      lg: 'size-6',
      xl: 'size-7',
    },
  },
  defaultVariants: {
    radioSize: 'md',
  },
} as const;

const radioGroupItemVariants = cva({
  base: RADIO_GROUP_ITEM_CLASSES,
  ...radioVariants,
});

type InputVariants = VariantProps<typeof inputVariants>;

type OtpInputSlotVariants = VariantProps<typeof otpInputSlotVariants>;

type CheckboxVariants = VariantProps<typeof checkboxVariants>;

type RadioGroupItemVariants = VariantProps<typeof radioGroupItemVariants>;

function extractInputVariants<T extends InputVariants>(props: T) {
  const { styleProps, others } = extractStyleProps(props);
  const otherProps = Object.keys(others).reduce(
    (acc, key) => {
      if (key in shared.variants) {
        acc[key as keyof InputVariants] = others[key as keyof typeof others];
      }
      return acc;
    },
    {} as Record<string, unknown>,
  );
  return {
    ...styleProps,
    ...otherProps,
  } as InputVariants;
}

function extractRadioGroupItemVariants<T extends RadioGroupItemVariants>(
  props: T,
) {
  const { styleProps, others } = extractStyleProps(props);
  const otherProps = Object.keys(others).reduce(
    (acc, key) => {
      if (key in radioVariants.variants) {
        acc[key as keyof RadioGroupItemVariants] =
          others[key as keyof typeof others];
      }
      return acc;
    },
    {} as Record<string, unknown>,
  );
  return {
    ...styleProps,
    ...otherProps,
  } as RadioGroupItemVariants;
}

export {
  type CheckboxVariants,
  checkboxVariants,
  extractInputVariants,
  extractRadioGroupItemVariants,
  inputContainerVariants,
  type InputVariants,
  inputVariants,
  type OtpInputSlotVariants,
  otpInputSlotVariants,
  type RadioGroupItemVariants,
  radioGroupItemVariants,
};
